import React, { useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Badge,
  Button,
  AppBar,
  Grid,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@material-ui/core';
import moment from 'moment';
import MaterialTable, { Query, QueryResult } from 'material-table';
import { Link } from 'react-router-dom';
import removeMarkdown from 'remove-markdown';
import { Close, OpenInNew, Check } from '@material-ui/icons';

import AdminLayout from '../../../layouts/AdminLayout';
import SpecComment from '../../../models/SpecComment';
import SpecCommentStatus from '../../../models/SpecCommentStatus';
import SpecCommentReport from '../../../models/SpecCommentReport';
import SpecFeedback from '../../../models/SpecFeedback';
import { CommentItem } from '../../../components/Spec/SpecComments';
import { GenericModal } from '../../../components/Modal';
import { useDialog } from '../../../context/Dialog';
import useSpecComments from '../../../hooks/useSpecComments';
import useLoadingStatus from '../../../hooks/useLoadingStatus';
import utils from '../../../libs/utils';

const SpecComments = () => {
  const [activeTab, setActiveTab] = useState<string>('comments');
  const [activeReport, setActiveReport] = useState<SpecCommentReport|null>(null);
  const [activeFeedback, setActiveFeedback] = useState<SpecFeedback|null>(null);
  const { isLoading: isResolving, executeWithStatus: resolveWithStatus } = useLoadingStatus();
  const { isLoading: isDeleting, executeWithStatus: deleteWithStatus } = useLoadingStatus();
  const { isLoading: isResolvingFeedback, executeWithStatus: resolveFeedbackWithStatus } = useLoadingStatus();
  const { alert, confirm } = useDialog();

  const {
    open: openSpecCommentsModal,
    renderModal: renderSpecCommentsModal,
  } = useSpecComments();

  const loadComments = async (query: Query<SpecCommentStatus>): Promise<QueryResult<SpecCommentStatus>> => {
    const { items, paging } = await SpecCommentStatus.find({
      $page: query.page + 1,
      $limit: query.pageSize
    });
    return {
      data: items,
      page: paging.currentPage - 1,
      totalCount: paging.totalCount
    };
  };

  const loadCommentReports = async (query: Query<SpecCommentReport>): Promise<QueryResult<SpecCommentReport>> => {
    const { items, paging } = await SpecCommentReport.find({
      $page: query.page + 1,
      $limit: query.pageSize
    });

    return {
      data: items,
      page: paging.currentPage - 1,
      totalCount: paging.totalCount
    };
  };

  const loadFeedbacks = async (query: Query<SpecFeedback>): Promise<QueryResult<SpecFeedback>> => {
    const { items, paging } = await SpecFeedback.find({
      $page: query.page + 1,
      $limit: query.pageSize,
      $orderBy: query.orderBy?.field,
      $orderDirection: query.orderBy?.field ? query.orderDirection || 'asc' : undefined
    });

    return {
      data: items,
      page: paging.currentPage - 1,
      totalCount: paging.totalCount
    };
  };

  const deleteComment = async (comment: SpecComment) => {
    await deleteWithStatus(async () => {
      const confirmed = await confirm('Are you sure to resolve the report by deleting the comment?', {
        title: 'Delete Confirmation'
      });
      if (!confirmed) return;
      await comment.destroy();
      await activeReport?.resolve();
      setActiveReport(null);
    }, e => alert(utils.getRequestErrorMessage(e),{
      title: 'Delete Comment Error'
    }));
  };

  const resolveReport = async (report: SpecCommentReport) => {
    await resolveWithStatus(async () => {
      const confirmed = await confirm('Are you sure to resolve the report?', {
        title: 'Resolve Confirmation'
      });
      if (!confirmed) return;
      await report.resolve();
      setActiveReport(null);
    }, e => alert(utils.getRequestErrorMessage(e),{
      title: 'Resolve Report Error'
    }));
  };

  const resolveFeedback = async (feedback: SpecFeedback) => {
    await resolveFeedbackWithStatus(async () => {
      const confirmed = await confirm('Are you sure to resolve the feedback?', {
        title: 'Resolve Confirmation'
      });
      if (!confirmed) return;
      await feedback.setStatus('resolved');
      setActiveFeedback(null);
    }, e => alert(utils.getRequestErrorMessage(e),{
      title: 'Resolve Feedback Error'
    }));
  };

  return (
    <>
      {renderSpecCommentsModal({
        onCommentRead: () => {
          setActiveTab('');
          setTimeout(() => setActiveTab(activeTab));
        }
      })}

      <GenericModal
        open={!!activeReport}
        onClose={() => setActiveReport(null)}
        header={
          <Box p={1}>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h5">Reported Note</Typography>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={() => setActiveReport(null)}>
                  <Close />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        }
        footer={
          <Box p={1} textAlign="right">
            <Grid container spacing={1} justify="flex-end">
              {!!activeReport && (
                <Grid item>
                  <Tooltip title="Ignore and resolve the report">
                    <Button
                      onClick={() => resolveReport(activeReport)}
                      startIcon={isResolving && <CircularProgress size="1em" color="inherit" />}
                      variant="outlined"
                      size="small"
                      color="primary"
                      disabled={isResolving}
                    >
                      Resolve Report
                    </Button>
                  </Tooltip>
                </Grid>
              )}

              {!!activeReport?.comment && (
                <Grid item>
                  <Tooltip title="Delete and resolve the report">
                    <Button
                      onClick={() => activeReport?.comment ? deleteComment(activeReport.comment) : undefined}
                      startIcon={isDeleting && <CircularProgress size="1em" color="inherit" />}
                      variant="outlined"
                      size="small"
                      color="primary"
                      disabled={isDeleting}
                    >
                      Delete Note
                    </Button>
                  </Tooltip>
                </Grid>
              )}

              {!!activeReport?.comment?.specId && (
                <Grid item>
                  <Tooltip title="Open the comment thread">
                    <Button
                      onClick={() => openSpecCommentsModal({specId: activeReport?.comment?.specId || ''})}
                      variant="contained"
                      size="small"
                      color="primary">
                      Open Thread
                    </Button>
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          </Box>
        }
      >
        {!!activeReport?.comment && (
          <>
            <CommentItem comment={activeReport.comment} showMenu={false} divider={true} />
            <Box px={2} pt={2} pb={1}>
              <Typography color="error">{activeReport.description}</Typography>
            </Box>
          </>
        )}
      </GenericModal>

      <GenericModal
        open={!!activeFeedback}
        onClose={() => setActiveFeedback(null)}
        header={
          <Box p={1}>
            <Grid container justify="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h5">Spec Feedback</Typography>
              </Grid>
              <Grid item>
                <IconButton size="small" onClick={() => setActiveFeedback(null)}>
                  <Close />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        }
        footer={
          <Box p={1} textAlign="right">
            {!!activeFeedback && (
              <Grid container spacing={1} justify="flex-end">
                <Grid item>
                  {(() => {
                    if (activeFeedback?.status === 'resolved')
                      return (
                        <Button startIcon={<Check />} disabled={true}>
                          Feedback Resolved
                        </Button>
                      );

                    return (
                      <Tooltip title="Change status to resolved">
                        <Button
                          startIcon={isResolvingFeedback && <CircularProgress size="1em" color="inherit" />}
                          variant="outlined"
                          size="small"
                          color="primary"
                          disabled={isResolvingFeedback}
                          onClick={() => resolveFeedback(activeFeedback)}
                        >
                          Resolve Feedback
                        </Button>
                      </Tooltip>
                    );
                  })()}
                </Grid>
              </Grid>
            )}
          </Box>
        }
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>
                <Typography>Spec</Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  <Link to={`/admin/specs/${activeFeedback?.specId}`} target="_blank">{activeFeedback?.specId} <OpenInNew fontSize="inherit" style={{lineHeight: 1, verticalAlign: 'middle'}} /></Link>
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <Typography>Reported By</Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  {activeFeedback?.author?.fullName}
                </Typography>
              </TableCell>
            </TableRow>
            {!!activeFeedback?.createdAt && (
              <TableRow>
                <TableCell>
                  <Typography>Reported At</Typography>
                </TableCell>
                <TableCell>
                  <Typography>
                    {moment(activeFeedback?.createdAt).format('MMM DD, YYYY hh:mm a')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell style={{border: 'none'}}>
                <Typography>Description</Typography>
              </TableCell>
              <TableCell style={{border: 'none'}}>
                <Typography>{activeFeedback?.description}</Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </GenericModal>

      <AdminLayout maxWidth={false} disableGutters={true}>
        <AppBar position="static" color="transparent">
          <Tabs value={activeTab} onChange={(e, tab) => setActiveTab(tab)} indicatorColor="primary">
            <Tab label="Notes" value="comments" />
            <Tab label="Reported Notes" value="reports" />
            <Tab label="Feedbacks" value="feedbacks" />
          </Tabs>
        </AppBar>

        <Box px={3} pt={3} pb={12}>
          {(() => {
            if (activeTab === 'comments')
              return (
                <MaterialTable
                  key="comments"
                  title="Notes"
                  data={loadComments}
                  columns={[
                    {
                      title: 'Spec',
                      field: 'specName',
                      render: s => (<Link to={`/admin/specs/${s.specId}`}>{s.specId}</Link>)
                    },
                    {
                      title: 'Latest Author',
                      field: 'author.fullName'
                    },
                    {
                      title: 'Latest Update',
                      field: 'createdAt',
                      render: s => moment(s.createdAt).fromNow()
                    },
                    {
                      title: 'Latest Note',
                      field: 'content',
                      render: (s) => (
                        <div
                          style={{
                            maxWidth: '200px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginRight: '10px'
                          }}
                        >
                          {removeMarkdown(s.content, {
                            gfm: true
                          })}
                        </div>
                      )
                    },
                    {
                      width: '0',
                      render: (s) => (
                        <Badge
                          color="primary"
                          badgeContent={s.unseenTotal}
                        >
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => openSpecCommentsModal({specId: s.specId})}>
                            Open Thread
                          </Button>
                        </Badge>
                      )
                    }
                  ]}
                  localization={{
                    body: {
                      emptyDataSourceMessage: 'No spec notes yet.'
                    }
                  }}
                  options={{
                    headerStyle: {
                      fontWeight: 'bold',
                    },
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 20, 50],
                    emptyRowsWhenPaging: false,
                    search: false,
                    sorting: false,
                    draggable: false
                  }}
                />
              );

            if (activeTab === 'reports' && !isResolving && !isDeleting)
              return (
                <MaterialTable
                  key="reports"
                  title="Reported Notes"
                  data={loadCommentReports}
                  columns={[
                    {
                      title: 'Spec',
                      field: 'comment.specId',
                      render: r => (<Link to={`/admin/specs/${r.comment?.specId}`}>{r.comment?.specId}</Link>)
                    },
                    {
                      title: 'Reporter',
                      field: 'reporter.fullName'
                    },
                    {
                      title: 'Reported',
                      field: 'createdAt',
                      render: c => moment(c.createdAt).fromNow()
                    },
                    {
                      title: 'Note',
                      field: 'comment.content',
                      render: (report) => (
                        <div
                          style={{
                            maxWidth: '200px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginRight: '10px'
                          }}
                        >
                          {removeMarkdown(report.comment?.content || '', {
                            gfm: true
                          })}
                        </div>
                      )
                    },
                    {
                      title: 'Description',
                      field: 'description',
                      render: (report) => (
                        <div
                          style={{
                            maxWidth: '200px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginRight: '10px'
                          }}
                        >
                          {report.description}
                        </div>
                      )
                    },
                    {
                      width: '0',
                      render: (report) => (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => setActiveReport(report)}>
                          Open Report
                        </Button>
                      )
                    }
                  ]}
                  localization={{
                    body: {
                      emptyDataSourceMessage: 'No note reports yet.'
                    }
                  }}
                  options={{
                    headerStyle: {
                      fontWeight: 'bold',
                    },
                    initialPage: 0,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 20, 50],
                    emptyRowsWhenPaging: false,
                    search: false,
                    sorting: false,
                    draggable: false
                  }}
                />
              );

            if (activeTab === 'feedbacks' && !isResolvingFeedback)
              return (
                <MaterialTable
                  key="feedbacks"
                  title="Feedbacks"
                  data={loadFeedbacks}
                  columns={[
                    {
                      title: 'Spec',
                      field: 'spec_id',
                      render: f => (<Link to={`/admin/specs/${f.specId}`}>{f.specId}</Link>)
                    },
                    {
                      title: 'Author',
                      field: 'author.fullName',
                      sorting: false
                    },
                    {
                      title: 'Reported',
                      field: 'created_at',
                      render: f => moment(f.createdAt).fromNow()
                    },
                    {
                      title: 'Description',
                      field: 'description',
                      render: (feedback) => (
                        <div
                          style={{
                            maxWidth: '200px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            marginRight: '10px'
                          }}
                        >
                          {feedback.description}
                        </div>
                      )
                    },
                    {
                      title: 'Status',
                      field: 'status',
                      lookup: { 'pending': 'Pending', 'resolved': 'Resolved' }
                    },
                    {
                      width: '0',
                      render: (feedback) => (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={() => setActiveFeedback(feedback)}
                        >
                          Open Feedback
                        </Button>
                      )
                    }
                  ]}
                  localization={{
                    body: {
                      emptyDataSourceMessage: 'No feedbacks for now.'
                    }
                  }}
                  options={{
                    headerStyle: {
                      fontWeight: 'bold',
                    },
                    initialPage: 0,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 20, 50],
                    emptyRowsWhenPaging: false,
                    search: false,
                    sorting: true,
                    thirdSortClick: false,
                    draggable: false
                  }}
                />
              );

          })()}
        </Box>
      </AdminLayout>
    </>
  );
};

export default SpecComments;
